import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import type { BBox, Restroom, RestroomFilters } from "../types";
import { fetchRestrooms } from "./api";

/**
 * Kolik WC načítáme na jeden výřez. Server umí až 1000, ale při oddálení na
 * celou zemi je i tohle jen zlomek dat – proto `truncated` (viz níž).
 */
const LIMIT = 500;

/**
 * Načítá WC podle viditelného výřezu mapy a drží stav pro shlukování
 * (`bounds`/`zoom`). Načítání je debouncované (300 ms).
 *
 * `truncated` říká, že výřez obsahuje víc WC, než kolik jich server vrátil –
 * bez něj mapa při oddálení tiše zamlčí většinu bodů a počítadlo lže.
 *
 * **Filtry patří do dotazu, ne za něj.** Dotaz je omezený `LIMIT` bez
 * `ORDER BY`, takže vrací nejnižší `rowid` – ne reprezentativní vzorek výřezu.
 * Filtrovat až vrácený seznam proto znamenalo filtrovat libovolnou podmnožinu:
 * nad ČR + Rakouskem hlásilo „♿ bezbariérové" 153 bodů z 500 vrácených, ačkoli
 * jich ve výřezu byly tisíce. Změna filtru je tedy nový fetch.
 */
export function useRestroomsInView(
  mapRef: RefObject<MapRef | null>,
  filters: RestroomFilters,
) {
  const [restrooms, setRestrooms] = useState<Restroom[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [bounds, setBounds] = useState<BBox | null>(null);
  const [zoom, setZoom] = useState(14);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Pořadí requestů: odpovědi se můžou křížit a bez tohohle by stav přepsala
  // ta, která doběhla poslední, ne ta, která byla vyžádaná poslední.
  const latestRequest = useRef(0);

  // Aktualizuje výřez/zoom pro clustering podle aktuálního stavu mapy.
  const syncView = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    setBounds([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]);
    setZoom(map.getZoom());
  }, [mapRef]);

  // Načte WC podle viditelného výřezu (debounce 300 ms).
  const loadForBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const bbox: BBox = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      const request = ++latestRequest.current;
      fetchRestrooms(bbox, LIMIT, filters)
        .then((list) => {
          if (request !== latestRequest.current) return; // předběhla ji novější
          setRestrooms(list);
          setTruncated(list.length >= LIMIT);
        })
        .catch(console.error);
    }, 300);
  }, [mapRef, filters]);

  // Filter changes fetch server-filtered data. The initial call may run before
  // MapRef exists; RestroomMap initializes data when the instance becomes ready.
  useEffect(() => {
    loadForBounds();
  }, [loadForBounds]);

  useEffect(() => () => {
    if (debounce.current) clearTimeout(debounce.current);
  }, []);

  // Optimisticky přidá nově vytvořené WC do seznamu (bez dalšího fetche).
  const addRestroom = useCallback((restroom: Restroom) => {
    setRestrooms((prev) => [...prev, restroom]);
  }, []);

  return { restrooms, truncated, bounds, zoom, syncView, loadForBounds, addRestroom };
}
